import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useCurrentUser } from 'api/hooks/useSession'
import { queryClient, invalidateCompanyScopedQueries } from 'api/queryClient'

/**
 * Lean replacement for the data-heavy CompanyContext.
 *
 * Stores ONLY the active companyId. Company data itself is fetched via
 * `useActiveCompany()` (composes useCompany), which keys on companyId and
 * therefore swaps caches automatically when the selection changes.
 *
 * Persists the selection in localStorage under `actualCompanyID${userId}` to
 * match the existing key shape (so a user revisiting after migration lands on
 * the same company they had selected).
 */

const ActiveCompanyContext = createContext({
    companyId: undefined,
    setCompanyId: () => {},
})

function readStoredCompanyId(userId) {
    if (!userId) return undefined
    try {
        const raw = localStorage.getItem(`actualCompanyID${userId}`)
        // Ignore non-numeric/corrupt values (e.g. "[object Object]") so a
        // poisoned key self-heals from user.companies on next load.
        return raw && /^\d+$/.test(raw) ? Number(raw) : undefined
    } catch (_) {
        return undefined
    }
}

export function ActiveCompanyProvider({ children }) {
    const { data: user } = useCurrentUser()
    const [companyId, _setCompanyId] = useState(undefined)

    // When the user loads, pick the stored selection or fall back to the first
    // company on the profile.
    useEffect(() => {
        if (!user?.id) return
        const stored = readStoredCompanyId(user.id)
        const valid =
            stored &&
            user.companies?.some(
                (c) => String(c.id) === String(stored)
            )
                ? stored
                : user.companies?.[0]?.id
        if (valid && String(valid) !== String(companyId)) {
            _setCompanyId(valid)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id])

    const setCompanyId = useCallback(
        (next) => {
            if (!user?.id || !next) return
            // Accept either an id or a company object; never store anything else.
            const nextId = typeof next === 'object' ? next?.id : next
            if (nextId == null || !/^\d+$/.test(String(nextId))) return
            if (String(nextId) === String(companyId)) return
            try {
                localStorage.setItem(
                    `actualCompanyID${user.id}`,
                    String(nextId)
                )
            } catch (_) {}
            invalidateCompanyScopedQueries()
            _setCompanyId(nextId)
        },
        [companyId, user?.id]
    )

    const value = useMemo(
        () => ({ companyId, setCompanyId }),
        [companyId, setCompanyId]
    )

    return (
        <ActiveCompanyContext.Provider value={value}>
            {children}
        </ActiveCompanyContext.Provider>
    )
}

export function useActiveCompanyId() {
    return useContext(ActiveCompanyContext).companyId
}

export function useSetActiveCompanyId() {
    return useContext(ActiveCompanyContext).setCompanyId
}

export function useActiveCompanyContext() {
    return useContext(ActiveCompanyContext)
}

export default ActiveCompanyContext

// Re-exported for callers that need to reach the cache directly when changing
// companies (e.g. on first sign-in).
export { queryClient }
